// Display helpers for the Feedback tab. Own module (like cpSourcingFormat /
// cpSourcingTheme) so the desktop grid, the mobile card and the details modal can
// all import them without importing each other.
//
// Every field the vendor can return null for passes through here, because the
// sandbox data really does contain those nulls: fv_booking_id null in 54 of 98
// records, extra_info null in 8, referred_by_name null in 3, mobile null in 1,
// and rating null on everything still PENDING.

// The app's convention for a missing value in a table cell.
export const dash = (v) =>
  v === null || v === undefined || String(v).trim() === "" ? "-" : String(v);

// Contact details are shown to staff who only need to recognise the record, not
// to read the number or address back, so both are partially hidden.
//
// Mobile: only the last five characters stay visible, however long the value is,
// so a longer number does not leak more of itself — "9860076254" → "*****76254"
// and "+919860076254" → "********76254". Anything five characters or shorter is
// masked whole rather than left effectively readable.
export const maskMobile = (v) => {
  const s = v === null || v === undefined ? "" : String(v).trim();
  if (!s) return "-";
  if (s.length <= 5) return "*".repeat(s.length);
  return "*".repeat(s.length - 5) + s.slice(-5);
};

// Email: every second character of each run is replaced, so the shape of the
// address survives but the address itself is not readable. The separators
// @ . - _ + stay visible and restart the alternation, which keeps every run
// starting on a readable character:
//   "pratik.gaikwad@payasystems.com" → "p*a*i*.g*i*w*d@p*y*s*s*e*s.c*m"
// A value with no "@" is masked the same way, so a malformed address is not
// accidentally shown in full.
export const maskEmail = (v) => {
  const s = v === null || v === undefined ? "" : String(v).trim();
  if (!s) return "-";
  return s.replace(/[^@._+-]+/g, (run) =>
    run
      .split("")
      .map((ch, i) => (i % 2 ? "*" : ch))
      .join("")
  );
};

// ISO 8601 from the vendor (e.g. "2026-08-18T09:06:59.015Z") → "18 Aug 2026, 02:36 PM".
// Rendered in the viewer's local timezone, matching fmtDateTime in CP Sourcing.
export const fmtDateTime = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? "-"
    : d.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

// Date only, for the grid's Sent At column where the time would crowd the cell.
export const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? "-"
    : d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

// Response status. `status` is the source of truth — deliberately NOT derived
// from `delivered` or `submitted_at`, because PENDING + delivered:true +
// submitted_at:null is a valid, common state (delivery ≠ response).
// Colours are Leads' chip palette, same values statusPalette uses.
export const statusPalette = (status) =>
  status === "SUBMITTED"
    ? { bg: "#ECFDF3", color: "#027A48", dot: "#027A48", label: "Submitted" }
    : status === "PENDING"
    ? { bg: "#FFFAEB", color: "#B54708", dot: "#B54708", label: "Pending" }
    // Unknown future status: show the vendor's own string rather than mislabel it.
    : { bg: "#F2F4F7", color: "#667085", dot: "#667085", label: dash(status) };

// Delivery status, a separate axis from response status. Applies to the
// per-channel whatsapp_status / email_status values (SENT / SKIPPED / FAILED
// observed) as well as the overall delivered boolean.
export const deliveryPalette = (value) => {
  if (value === true || value === "SENT")
    return { bg: "#ECFDF3", color: "#027A48", dot: "#027A48", label: value === true ? "Delivered" : "Sent" };
  if (value === false)
    return { bg: "#FEF3F2", color: "#B42318", dot: "#B42318", label: "Not Delivered" };
  if (value === "FAILED")
    return { bg: "#FEF3F2", color: "#B42318", dot: "#B42318", label: "Failed" };
  if (value === "SKIPPED")
    return { bg: "#F2F4F7", color: "#667085", dot: "#667085", label: "Skipped" };
  return { bg: "#F2F4F7", color: "#667085", dot: "#667085", label: dash(value) };
};

// Rating is null while feedback is pending, so "-" rather than "0".
export const fmtRating = (rating) =>
  rating === null || rating === undefined ? "-" : `${rating}/5`;

// extra_info is an already-parsed object — never JSON.parse it. It can be null,
// {}, or carry different key-sets between records (headRow1/headRow2 on most,
// plus headRow3/content/meta on some). Flattened to label/value rows for generic
// rendering; no assumption that headRow1 is a project or headRow2 a unit, since
// that mapping is unconfirmed.
export const extraInfoRows = (extraInfo) => {
  if (!extraInfo || typeof extraInfo !== "object") return [];
  return Object.entries(extraInfo)
    .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== "")
    .map(([key, value]) => ({
      key,
      label: humanizeKey(key),
      value: typeof value === "object" ? JSON.stringify(value) : String(value),
    }));
};

// "headRow1" → "Head Row 1", "ref_type" → "Ref Type". Only cosmetic: the raw key
// is kept as `key` so nothing depends on this transformation.
export const humanizeKey = (key) =>
  String(key)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([a-zA-Z])(\d)/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

// MULTI_CHOICE answers arrive as a JSON string inside answer_text rather than
// through option_label — e.g. '[{"id":10,"label":"first option"}]'. Rendered raw
// that JSON reaches the user verbatim, so the labels are pulled out. Anything
// that does not parse falls back to the original text unchanged.
const choiceLabels = (text) => {
  if (typeof text !== "string") return text;
  const trimmed = text.trim();
  if (!trimmed.startsWith("[") && !trimmed.startsWith("{")) return text;
  try {
    const parsed = JSON.parse(trimmed);
    const list = Array.isArray(parsed) ? parsed : [parsed];
    const labels = list
      .map((o) => o && (o.label ?? o.option_label ?? o.value ?? o.option_value))
      .filter((l) => l !== null && l !== undefined && String(l).trim() !== "");
    return labels.length ? labels.join(", ") : text;
  } catch {
    return text;
  }
};

// One answer can be a rating, free text, a boolean, a single chosen option or a
// multi-choice selection. The vendor populates whichever column applies and
// leaves the rest null.
//
// Observed question_type values on the live API: RATING, TEXT, MULTI_CHOICE.
// The spec also lists YES_NO and SINGLE_CHOICE, so both are still handled.
export const answerValue = (answer) => {
  if (!answer) return "-";
  if (answer.answer_rating !== null && answer.answer_rating !== undefined)
    return `${answer.answer_rating}/5`;
  if (answer.answer_boolean !== null && answer.answer_boolean !== undefined)
    return answer.answer_boolean ? "Yes" : "No";
  if (answer.option_label) return answer.option_label;
  return dash(choiceLabels(answer.answer_text));
};

// The customer's overall free-text comment. Present on every record as
// `feedback_answer` (string or null) — separate from the per-question answers,
// and populated on records that have no answers at all. Note the read endpoints
// disagree on the name: /sent calls it feedback_answer, /responses feedback_text.
export const commentText = (row) => {
  const value = row?.feedback_answer ?? row?.feedback_text;
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
};

// A date-only picker gives a local Date; the vendor's from_date/to_date want a
// plain calendar day. Formatted from local parts so a late-evening pick does not
// slide to the next day the way toISOString() would.
export const toApiDate = (value) => {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};
